import { LocationData, Device, DeviceOperation } from '../features/device/types/device';

/**
 * Parses location data from device location.data string
 * @param locationData - JSON string containing location information
 * @returns Parsed LocationData object or null if parsing fails
 */
export const parseLocationData = (locationData: string | null | undefined): LocationData | null => {
  if (!locationData) return null;
  
  try {
    const parsed = JSON.parse(locationData);
    return {
      Station: parsed.StationNumber || '',
      BugType: parsed.BugType || '',
      BugNum: parsed.BugNum || ''
    };
  } catch (error) {
    console.warn('Failed to parse location data:', error);
    return null;
  }
};

/**
 * Parses location data from device location.data string
 * @param locationData - JSON string containing location information
 * @returns Parsed LocationData object or null if parsing fails
 */
export const parseNumberOfLogsData = (numberOfLogsData: string | null | undefined): any | null => {
  if (!numberOfLogsData) return null;
  
  try {
    const parsed = JSON.parse(numberOfLogsData);
    return {
      numberOfLogs: parsed.numberOfLogs || parsed.NumberOfLogs || '',
      lastChecked: parsed.lastChecked || parsed.LastChecked || ''
    };
  } catch (error) {
    console.warn('Failed to parse number of logs data:', error);
    return null;
  }
};



/**
 * Formats location display string
 * @param locationData - Parsed location data
 * @returns Formatted location string
 */
export const formatLocationString = (locationData: LocationData | null): string => {
  if (!locationData) return 'No location data';
  
  const parts = [];
  if (locationData.Station) parts.push(`Station ${locationData.Station}`);
  if (locationData.BugType) parts.push(locationData.BugType);
  if (locationData.BugNum) parts.push(`Bug ${locationData.BugNum}`);
  
  return parts.join(' - ') || 'No location data';
};

/**
 * Groups devices by station
 * @param devices - Array of devices
 * @returns Object with station as key and devices array as value
 */
export const groupDevicesByStation = (devices: Device[]) => {
  const grouped: { [key: string]: Device[] } = {};
  
  devices.forEach(device => {
    // Use parsed location data if available, otherwise parse from data string
    const locationData = device.location?.parsed || parseLocationData(device.location?.data);
    const station = locationData?.Station || 'Unknown';
    
    if (!grouped[station]) {
      grouped[station] = [];
    }
    grouped[station].push(device);
  });
  
  return grouped;
};

/**
 * Device operation field names and their display names
 */
export const DEVICE_OPERATION_FIELDS = {
  set_time: 'Set Time',
  get_time: 'Get Time',
  erase_logs: 'Erase Logs',
  number_of_logs: 'Number of Logs',
  password_protection: 'Password Protection',
  weld_parameter_logs: 'Weld Parameter Logs'
} as const;

/**
 * Extracts all errors from device operations
 * @param device - Device object
 * @returns Array of error objects with field name, display name, and error message
 */
export const getDeviceErrors = (device: Device) => {
  const errors: Array<{
    field: string;
    displayName: string;
    error: string;
  }> = [];

  Object.entries(DEVICE_OPERATION_FIELDS).forEach(([field, displayName]) => {
    const operation = device[field as keyof Device] as DeviceOperation | undefined;
    if (operation?.error) {
      errors.push({
        field,
        displayName,
        error: operation.error
      });
    }
  });

  return errors;
};

/**
 * Gets the most critical error from device operations
 * @param device - Device object
 * @returns The first error found or null
 */
export const getPrimaryDeviceError = (device: Device) => {
  const errors = getDeviceErrors(device);
  return errors.length > 0 ? errors[0] : null;
};

/**
 * Processes a device and parses its location data
 * @param device - Device object with location.data as JSON string
 * @returns Device with parsed location data
 */
export const processDeviceLocation = (device: Device): Device => {
  if (device.location?.data) {
    const parsedLocation = parseLocationData(device.location.data);
    return {
      ...device,
      location: {
        ...device.location,
        parsed: parsedLocation || undefined
      }
    };
  }
  return device;
};

/**
 * Processes an array of devices and parses their location data
 * @param devices - Array of device objects
 * @returns Array of devices with parsed location data
 */
export const processDevicesLocation = (devices: Device[]): Device[] => {
  return devices.map(processDeviceLocation);
};
